import { FitsImage } from '@/modules/FitsImageModule/FitsImage';
import { store } from '@/store';
import { Bridge } from '..';
import { decode as b64decode } from "base64-arraybuffer";
import { ICallback } from './callback';
import { TryLater } from "@/utils/try_later";

interface IAddFits {
  type: 'add_fits'
  args: {
    id: string,
    buf: string,
    name: string,
    tone: FitsImage["tone"] | null,
    tone_window: boolean,
    on_change: ICallback,
  }
}

interface IDeleteFits {
  type: 'remove_fits'
  args: {
    id: string,
  }
}

interface IUpdateFits {
  type: 'update_fits',
  args: {
    id: string,
    name: string,
    tone: FitsImage["tone"] | null,
    tone_window: boolean,
  },
}

class JFitsImage {
  constructor(readonly b: Bridge, readonly id: string, readonly onChange: ICallback, readonly fits: FitsImage) {
    store.currentFrame.modules.fitsImage.fitsImages.push(fits)
    this.setupWatchers()
  }

  static async fromMessage(b: Bridge, msg: IAddFits) {
    const { buf, id, name, on_change, tone, tone_window } = msg.args
    const fits = await FitsImage.fromArrayBuffer(name, b64decode(buf))
    tone && (fits.tone = tone)
    tone_window !== undefined && (fits.toneWindow = tone_window)
    return new JFitsImage(b, id, on_change, fits)
  }

  private cleanups: Array<() => void> = []

  private setupWatchers() {
    this.cleanups.push(
      this.fits.$watch(() => [this.fits.name, this.fits.tone, this.fits.toneWindow], ([name, tone, toneWindow]) => {
        this.b.callback(this.onChange, [{ name, tone, tone_window: toneWindow }])
      }, { deep: true }),
    )
  }

  remove() {
    while (this.cleanups.length > 0) {
      this.cleanups.pop()!()
    }
    const a = store.currentFrame.modules.fitsImage.fitsImages
    a.splice(a.indexOf(this.fits), 1)
    store.currentGlobe().requestRefresh()
  }

  update(msg: IUpdateFits) {
    const { name, tone, tone_window } = msg.args
    this.fits.name = name
    tone !== null && (this.fits.tone = tone)
    this.fits.toneWindow = tone_window
  }
}

const registry: { [id: string]: JFitsImage } = {}
const tryLater = new TryLater()

export async function add_fits(b: Bridge, msg: IAddFits) {
  const jFits = await JFitsImage.fromMessage(b, msg)
  registry[jFits.id] = jFits
  tryLater.attempt()
}

export function remove_fits(b: Bridge, msg: IDeleteFits) {
  tryLater.register(() => {
    const { id } = msg.args
    const jFits = registry[id]
    if (jFits) {
      jFits.remove()
      delete registry[id]
      return TryLater.OK
    }
  })
}

export function update_fits(b: Bridge, msg: IUpdateFits) {
  tryLater.register(() => {
    const { id } = msg.args
    const jFits = registry[id]
    if (jFits) {
      jFits.update(msg)
      return TryLater.OK
    }
  })
}
